import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '', content: '', excerpt: '', image: '', author: '', is_published: true
  });

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/blog`, { withCredentials: true });
      setPosts(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData, { withCredentials: true });
        toast.success('Post updated!');
      } else {
        await axios.post(`${API}/blog`, formData, { withCredentials: true });
        toast.success('Post created!');
      }
      setShowForm(false);
      setEditingPost(null);
      setFormData({ title: '', content: '', excerpt: '', image: '', author: '', is_published: true });
      fetchPosts();
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/blog/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchPosts();
    } catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Blog / News</h1>
          <p className="text-charcoal/60 mt-1">Manage blog posts</p>
        </div>
        <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) setEditingPost(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-burgundy hover:bg-purple text-white">
              <Plus size={18} className="mr-2" /> New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingPost ? 'Edit Post' : 'New Blog Post'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required className="bg-cream" /></div>
              <div><Label>Author</Label><Input value={formData.author} onChange={(e) => setFormData({...formData, author: e.target.value})} className="bg-cream" /></div>
              <div><Label>Featured Image URL</Label><Input value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} placeholder="https://..." className="bg-cream" /></div>
              <div><Label>Excerpt</Label><Textarea value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} rows={2} className="bg-cream" placeholder="Brief summary..." /></div>
              <div><Label>Content</Label><Textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} rows={10} required className="bg-cream" placeholder="Write your post content here..." /></div>
              <Button type="submit" className="w-full bg-burgundy hover:bg-purple">{editingPost ? 'Update' : 'Publish'} Post</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : posts.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-2xl border border-gold/10 overflow-hidden">
              {post.image && <img src={post.image} alt={post.title} className="w-full h-40 object-cover" />}
              <div className="p-4">
                <h3 className="font-serif text-lg text-charcoal mb-1">{post.title}</h3>
                <p className="text-sm text-charcoal/60 mb-3 line-clamp-2">{post.excerpt || post.content?.substring(0, 100)}</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-charcoal/40">{post.author}</span>
                  <div>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingPost(post); setFormData(post); setShowForm(true); }}><Edit size={16} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)} className="text-red-500"><Trash2 size={16} /></Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-gold/10">
          <p className="text-charcoal/50">No blog posts yet. Write your first one!</p>
        </div>
      )}
    </div>
  );
}
